import { useEffect, useRef, useState } from "react";
import { COUNTRIES, findByIso } from "../../lib/countries";

const PhoneField = ({ label, testId, country, number, onCountryChange, onNumberChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const current = findByIso(country || "IN");

  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setQ("");
    }
  }, [open]);

  useEffect(() => {
    const onClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = q
    ? COUNTRIES.filter((c) => {
        const s = q.toLowerCase();
        return (
          c.name.toLowerCase().includes(s) ||
          c.dial.includes(s.replace(/^\+/, "")) ||
          c.iso.toLowerCase().includes(s)
        );
      })
    : COUNTRIES;

  const onDigit = (e) => {
    const cleaned = e.target.value.replace(/[^\d\s-]/g, "");
    onNumberChange(cleaned);
  };

  return (
    <label className="block" ref={wrapRef} data-testid={testId}>
      <span className="block text-[10px] tracking-[0.4em] uppercase text-cotton/55 font-light mb-3">
        {label}
      </span>

      <div className="relative flex items-end gap-3 border-b border-cotton/20 focus-within:border-red transition-colors pb-2 pt-1">
        {/* Country selector trigger */}
        <button
          type="button"
          data-testid={`${testId}-trigger`}
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 pr-3 border-r border-cotton/15 mr-1 py-2 hover:text-gold transition-colors"
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span className="text-xl leading-none">{current.flag}</span>
          <span className="font-condensed text-cotton text-lg tracking-wide">
            +{current.dial}
          </span>
          <span
            className={`text-cotton/40 text-xs transition-transform duration-300 ${
              open ? "rotate-180" : ""
            }`}
          >
            ▾
          </span>
        </button>

        {/* Phone digits */}
        <input
          data-testid={`${testId}-number`}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={number || ""}
          onChange={onDigit}
          placeholder="000 000 0000"
          className="flex-1 bg-transparent border-0 outline-none text-cotton font-condensed font-light text-lg py-2 placeholder:text-cotton/25"
        />
      </div>

      {/* Dropdown */}
      {open && (
        <div
          data-testid={`${testId}-dropdown`}
          className="relative z-30"
        >
          <div className="absolute left-0 right-0 mt-2 max-w-md border border-cotton/15 bg-[#0a0908] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.9)]">
            <div className="border-b border-cotton/10 px-3 py-2 flex items-center gap-2">
              <span className="text-cotton/35 text-xs">⌕</span>
              <input
                ref={searchRef}
                data-testid={`${testId}-search`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search country or +code…"
                className="flex-1 bg-transparent border-0 outline-none text-cotton font-condensed font-light text-sm placeholder:text-cotton/30"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  className="text-cotton/40 text-sm hover:text-red"
                  aria-label="clear"
                >
                  ×
                </button>
              )}
            </div>
            <ul
              role="listbox"
              className="max-h-64 overflow-y-auto py-1"
            >
              {filtered.length === 0 ? (
                <li className="px-4 py-3 text-cotton/40 text-sm italic">No matches</li>
              ) : (
                filtered.map((c) => {
                  const isCurrent = c.iso === current.iso;
                  return (
                    <li key={c.iso}>
                      <button
                        type="button"
                        data-testid={`${testId}-option-${c.iso.toLowerCase()}`}
                        onClick={() => {
                          onCountryChange(c.iso);
                          setOpen(false);
                        }}
                        className={`w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                          isCurrent
                            ? "bg-red/10 text-cotton"
                            : "text-cotton/80 hover:bg-cotton/[0.04] hover:text-cotton"
                        }`}
                      >
                        <span className="text-lg leading-none w-6">{c.flag}</span>
                        <span className="font-condensed flex-1 truncate">{c.name}</span>
                        <span className="font-condensed text-cotton/55 tabular-nums">+{c.dial}</span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </div>
      )}
    </label>
  );
};

export default PhoneField;
